import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import ColumnConfigDialog from './ColumnConfigDialog';
import { exportGridExcel, exportGridPdf } from '../../services/gridExport';
import './CadastroDataTable.css';

function isActionsField(field) {
  const f = String(field || '');
  return f === '_acoes' || f.startsWith('_acoes');
}

/** Padrão do app: Ações logo após o checkbox (início das colunas de dados). */
function withActionsFirst(fields) {
  if (!Array.isArray(fields) || !fields.length) return fields || [];
  const actions = [];
  const rest = [];
  fields.forEach((f) => {
    if (isActionsField(f)) actions.push(f);
    else rest.push(f);
  });
  return [...actions, ...rest];
}

function defaultVisibleFields(available) {
  return withActionsFirst(
    available
      .filter((c) => c.defaultVisible !== false)
      .map((c) => c.field)
      .filter(Boolean)
  );
}

function loadVisibleFields(storageKey, available) {
  const defaults = defaultVisibleFields(available);
  if (!storageKey) return defaults;
  try {
    const raw = localStorage.getItem(`nfse-grid-cols:${storageKey}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return defaults;
    const allowed = new Set(available.map((c) => c.field));
    const filtered = parsed.filter((f) => allowed.has(f));
    // inclui colunas alwaysVisible que não estavam salvas (Ações no início)
    available.forEach((c) => {
      if (c.alwaysVisible && c.field && !filtered.includes(c.field)) {
        if (isActionsField(c.field)) filtered.unshift(c.field);
        else filtered.push(c.field);
      }
    });
    return filtered.length ? filtered : defaults;
  } catch {
    return defaults;
  }
}

const ORDER_VERSION = '2'; // v2: Ações no início por padrão

function orderStorageKey(storageKey) {
  return `nfse-grid-order:${storageKey}`;
}

function orderVersionKey(storageKey) {
  return `nfse-grid-order-ver:${storageKey}`;
}

function ensureOrderVersion(storageKey) {
  if (!storageKey) return;
  try {
    const ver = localStorage.getItem(orderVersionKey(storageKey));
    if (ver === ORDER_VERSION) return;
    // Descarta ordem antiga (Ações no fim) — passa a valer o padrão novo
    localStorage.removeItem(orderStorageKey(storageKey));
    localStorage.setItem(orderVersionKey(storageKey), ORDER_VERSION);
  } catch {
    // ignore
  }
}

function loadOrder(storageKey, fields) {
  const fallback = withActionsFirst(fields);
  if (!storageKey) return fallback;
  try {
    ensureOrderVersion(storageKey);
    const raw = localStorage.getItem(orderStorageKey(storageKey));
    // Sem preferência de ordem do utilizador → Ações primeiro
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return fallback;
    const set = new Set(fields);
    const ordered = parsed.filter((f) => set.has(f));
    fields.forEach((f) => {
      if (!ordered.includes(f)) {
        if (isActionsField(f)) ordered.unshift(f);
        else ordered.push(f);
      }
    });
    return ordered;
  } catch {
    return fallback;
  }
}

/**
 * Grid padrão NFSeApp (padrão visual/UX do OSApp):
 * busca, configurar colunas, export Excel/PDF, reorder de colunas.
 *
 * columns: [{ field, header, body?, sortable?, style?, exportValue?, defaultVisible?, alwaysVisible? }]
 * Ordem padrão: [checkbox?] | Ações | demais colunas — até o utilizador rearranjar.
 */
export default function CadastroDataTable({
  value = [],
  columns = [],
  storageKey,
  loading = false,
  emptyMessage = 'Nenhum registro encontrado.',
  globalFilterFields,
  rows = 50,
  rowsPerPageOptions = [10, 25, 50, 100, 250],
  paginator = true,
  toolbarExtra = null,
  dataKey = 'id',
  title = 'Relatório',
  selection = null,
  onSelectionChange,
  selectionMode = null,
}) {
  const [configVisible, setConfigVisible] = useState(false);
  const [visibleFields, setVisibleFields] = useState(() => loadVisibleFields(storageKey, columns));
  const [orderFields, setOrderFields] = useState(() =>
    loadOrder(storageKey, loadVisibleFields(storageKey, columns))
  );
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const columnSignature = columns.map((c) => `${c.field}:${c.header || ''}`).join('|');

  useEffect(() => {
    const vis = loadVisibleFields(storageKey, columns);
    setVisibleFields(vis);
    setOrderFields(loadOrder(storageKey, vis));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, columnSignature]);

  const columnsByField = useMemo(() => {
    const map = {};
    columns.forEach((c) => {
      if (c?.field) map[c.field] = c;
    });
    return map;
  }, [columns]);

  const activeColumns = useMemo(() => {
    const vis = new Set(visibleFields);
    columns.forEach((c) => {
      if (c.alwaysVisible) vis.add(c.field);
    });
    const ordered = orderFields.filter((f) => vis.has(f) && columnsByField[f]);
    [...vis].forEach((f) => {
      if (!ordered.includes(f) && columnsByField[f]) {
        if (isActionsField(f)) ordered.unshift(f);
        else ordered.push(f);
      }
    });
    return ordered.map((f) => columnsByField[f]).filter(Boolean);
  }, [visibleFields, orderFields, columnsByField, columns]);

  const filterFields =
    globalFilterFields ||
    activeColumns.filter((c) => !String(c.field).startsWith('_')).map((c) => c.field);

  const onGlobalFilterChange = (e) => {
    const v = e.target.value;
    setFilters((prev) => ({ ...prev, global: { ...prev.global, value: v } }));
    setGlobalFilterValue(v);
  };

  const persistOrder = (next) => {
    setOrderFields(next);
    if (storageKey) {
      try {
        localStorage.setItem(orderStorageKey(storageKey), JSON.stringify(next));
        localStorage.setItem(orderVersionKey(storageKey), ORDER_VERSION);
      } catch {
        // ignore
      }
    }
  };

  const onColReorder = (e) => {
    const next = (e.columns || [])
      .map((col) => col.props?.field || col.field)
      .filter(Boolean);
    if (!next.length) return;
    persistOrder(next);
  };

  const handleExportExcel = () => {
    exportGridExcel({
      rows: value,
      columns: activeColumns.filter((c) => !String(c.field).startsWith('_')),
      fileName: storageKey || 'dados',
      sheetName: title,
    });
  };

  const handleExportPdf = async () => {
    await exportGridPdf({
      rows: value,
      columns: activeColumns.filter((c) => !String(c.field).startsWith('_')),
      fileName: storageKey || 'relatorio',
      title,
    });
  };

  return (
    <div className="cadastro-grid consulta-table-section">
      <div className="cadastro-grid-toolbar">
        <div className="cadastro-grid-toolbar-left">
          <div className="cadastro-grid-search">
            <i className="pi pi-search cadastro-grid-search-icon" aria-hidden />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Buscar..."
              className="cadastro-grid-search-input"
            />
          </div>
        </div>
        <div className="cadastro-grid-toolbar-right">
          {toolbarExtra}
          <Button
            label="Colunas"
            icon="pi pi-cog"
            className="p-button-outlined p-button-sm"
            onClick={() => setConfigVisible(true)}
            tooltip="Configurar colunas"
          />
          <Button
            label="Excel"
            icon="pi pi-file-excel"
            className="p-button-outlined p-button-sm"
            onClick={handleExportExcel}
            disabled={!value?.length}
          />
          <Button
            label="PDF"
            icon="pi pi-file-pdf"
            className="p-button-outlined p-button-sm"
            onClick={handleExportPdf}
            disabled={!value?.length}
          />
        </div>
      </div>

      <DataTable
        value={value}
        loading={loading}
        dataKey={dataKey}
        paginator={paginator}
        rows={rows}
        rowsPerPageOptions={rowsPerPageOptions}
        emptyMessage={emptyMessage}
        filters={filters}
        globalFilterFields={filterFields}
        reorderableColumns
        resizableColumns
        columnResizeMode="expand"
        onColReorder={onColReorder}
        className="consulta-grid cadastro-grid-table"
        stripedRows
        size="small"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        selection={selectionMode ? selection : undefined}
        onSelectionChange={selectionMode && onSelectionChange ? onSelectionChange : undefined}
        selectionMode={selectionMode || undefined}
      >
        {selectionMode === 'checkbox' && (
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} reorderable={false} />
        )}
        {activeColumns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={col.body}
            sortable={col.sortable !== false}
            style={col.style}
            headerStyle={col.headerStyle}
            reorderable={col.reorderable !== false}
          />
        ))}
      </DataTable>

      <ColumnConfigDialog
        visible={configVisible}
        onHide={() => setConfigVisible(false)}
        storageKey={storageKey}
        colunasDisponiveis={columns}
        colunasVisiveis={visibleFields}
        onSave={(fields, opts = {}) => {
          setVisibleFields(fields);
          setOrderFields((prev) => {
            const hadCustomOrder =
              !opts.resetOrder &&
              storageKey &&
              !!localStorage.getItem(orderStorageKey(storageKey));
            let next = prev.filter((f) => fields.includes(f));
            fields.forEach((f) => {
              if (!next.includes(f)) next.push(f);
            });
            // Sem ordem personalizada ou após "Restaurar padrão" → Ações primeiro
            if (!hadCustomOrder || opts.resetOrder) next = withActionsFirst(fields.filter(Boolean));
            if (storageKey) {
              try {
                localStorage.setItem(orderStorageKey(storageKey), JSON.stringify(next));
                localStorage.setItem(orderVersionKey(storageKey), ORDER_VERSION);
              } catch {
                // ignore
              }
            }
            return next;
          });
        }}
      />
    </div>
  );
}
